// Ajudantes mínimos de DOM. Sem framework.

import { icon } from "./icons.ts";

type Child = Node | string | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  for (const c of children) if (c || c === "") node.append(c as Node | string);
  return node;
}

/** Variantes de botão — seção "Componentes" de docs/design/ui.md. */
export type VarianteBotao =
  | "primario"
  | "secundario"
  | "perigo"
  | "perigo-solido"
  | "link"
  | "registro";
export type EstiloBotao = VarianteBotao | { variante: VarianteBotao; tamanho: "compacto" };

/**
 * O jeito de criar um botão de ação. A aparência vem da variante (classe
 * `btn btn-<variante>`), nunca de `type="submit"`. O tipo padrão é "button";
 * formulário que envia passa `{ type: "submit" }`. `attrs.class` soma
 * classes de layout (ex.: "btn-limpar"). Botão dentro de linha de tabela
 * fica compacto sozinho, pelo CSS.
 */
export function botao(
  estilo: EstiloBotao,
  attrs: Record<string, string> = {},
  ...children: Child[]
): HTMLButtonElement {
  const { variante, tamanho } =
    typeof estilo === "string" ? { variante: estilo, tamanho: undefined } : estilo;
  const classes = ["btn", `btn-${variante}`, tamanho === "compacto" ? "btn-compacto" : "", attrs.class ?? ""]
    .filter(Boolean)
    .join(" ");
  return el("button", { type: "button", ...attrs, class: classes }, ...children);
}

/**
 * Linha de situação com ícone — seção "Avisos" de docs/design/ui.md: alerta
 * antes de um problema, confere quando está tudo certo. O ícone é decorativo
 * (`aria-hidden`); o texto diz tudo sozinho.
 */
export function situacaoComIcone(tom: "alerta" | "ok", ...children: Child[]): HTMLElement {
  return el(
    "span",
    { class: `situacao-icone situacao-${tom}` },
    icon(tom === "alerta" ? "alerta" : "confere", 14),
    el("span", {}, ...children),
  );
}

export function clear(node: HTMLElement): void {
  node.textContent = "";
}

/** Tabela padrão do sistema, com rolagem horizontal própria. */
export function gridTable(
  headers: readonly string[],
  tbody: HTMLElement,
): HTMLElement {
  return el(
    "div",
    { class: "table-wrap" },
    el(
      "table",
      { class: "grid" },
      el("thead", {}, el("tr", {}, ...headers.map((h) => el("th", {}, h)))),
      tbody,
    ),
  );
}

/** Estado de uma tabela (paginação + ordenação), mantido pela tela. */
export interface EstadoTabela {
  page: number;
  size: number;
  ordCol: number | null;
  ordDir: "asc" | "desc";
}
export const novoEstadoTabela = (
  ordCol: number | null = null,
  ordDir: "asc" | "desc" = "asc",
): EstadoTabela => ({
  page: 1,
  size: 20,
  ordCol,
  ordDir,
});
/** @deprecated use novoEstadoTabela */
export const novaPaginacao = novoEstadoTabela;

const TAMANHOS = [20, 50, 100];

// --- ordenação -----------------------------------------------------------

function valorOrdenacao(tr: HTMLElement, col: number): string {
  const td = tr.children[col] as HTMLElement | undefined;
  if (!td) return "";
  return td.dataset["sort"] ?? td.textContent ?? "";
}

function comparar(a: string, b: string): number {
  const na = numeroPtBr(a);
  const nb = numeroPtBr(b);
  if (na !== null && nb !== null) return na - nb;
  return a.localeCompare(b, "pt-BR", { numeric: true, sensitivity: "base" });
}

function numeroPtBr(s: string): number | null {
  const limpo = s
    .trim()
    .replace(/^R\$\s*/i, "")
    .replace(/\./g, "")
    .replace(",", ".");
  if (limpo === "" || !/^-?\d+(\.\d+)?$/.test(limpo)) return null;
  return Number(limpo);
}

// --- larguras de coluna (persistidas por tabela) ------------------------

/** Tela estreita (celular): a tabela rola na horizontal em vez de comprimir. */
function telaEstreita(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(max-width: 700px)").matches
  );
}

/** Não deixa `v` passar de `[min, max]` — usado em todo arrasto/resize da
 * tela (modal, painel lateral, coluna de tabela): mesma conta, um nome só. */
function clamp(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), max);
}

/** Lê `chave` do localStorage como JSON; `padrao` cobre ausente, corrompido
 * ou storage indisponível — nunca lança. */
function lerJSON<T>(chave: string, padrao: T): T {
  try {
    const v = localStorage.getItem(chave);
    return v == null ? padrao : (JSON.parse(v) as T);
  } catch {
    return padrao;
  }
}
/** Como `lerJSON`, mas só aceita o valor se passar em `valido` — usa quando
 * um formato salvo errado (schema antigo, corrompido) não pode virar estado
 * inconsistente na tela (ex.: modal nascendo fora da janela). */
export function lerJSONValidado<T>(chave: string, valido: (v: unknown) => v is T): T | null {
  const v = lerJSON<unknown>(chave, null);
  return valido(v) ? v : null;
}
/** Escreve `valor` em `chave` do localStorage como JSON — nunca lança (sem
 * persistência é ok: storage pode estar indisponível, cheio, ou em modo
 * privado). */
export function salvarJSON(chave: string, valor: unknown): void {
  try {
    localStorage.setItem(chave, JSON.stringify(valor));
  } catch {
    /* sem persistência é ok */
  }
}

function lerLarguras(id: string): Record<number, number> {
  return lerJSON(`tab:${id}:larguras`, {});
}
function salvarLarguras(id: string, w: Record<number, number>): void {
  salvarJSON(`tab:${id}:larguras`, w);
}

/** Espaço guardado para a seta de ordenação, para a largura não pular ao ordenar. */
const RESERVA_SETA = 14;

/**
 * Nenhum cabeçalho pode sair cortado ("FATURAMENTC"). Numa tabela de largura
 * fixa as colunas sem `w` dividem o que sobrou em partes iguais, o que não
 * sabe nada sobre o tamanho do rótulo. Aqui a coluna cresce até caber o dela.
 * Só as colunas sem largura escolhida — `w` do código ou arrasto do usuário
 * continuam mandando.
 */
function ajustarCabecalhos(
  raiz: HTMLElement,
  colgroup: HTMLElement,
  jaTemLargura: (i: number) => boolean,
): void {
  // fora do documento nada tem largura: medir aqui só produziria número falso
  if (!raiz.isConnected) return;
  const ths = [...raiz.querySelectorAll<HTMLTableCellElement>("thead th")];
  // Leitura e escrita separadas (achado de performance): escrever
  // `col.style.width` no meio do loop forçava um reflow síncrono antes da
  // próxima leitura de `clientWidth`/`offsetWidth` — mede tudo primeiro,
  // só então aplica.
  const ajustes: { i: number; preciso: number }[] = [];
  ths.forEach((th, i) => {
    if (jaTemLargura(i)) return;
    const rotulo = th.querySelector<HTMLElement>(".th-rotulo");
    if (!rotulo) return;
    const seta = rotulo.querySelector<HTMLElement>(".th-seta");
    const estilo = getComputedStyle(th);
    const preciso =
      rotulo.offsetWidth -
      (seta?.offsetWidth ?? 0) +
      RESERVA_SETA +
      (parseFloat(estilo.paddingLeft) || 0) +
      (parseFloat(estilo.paddingRight) || 0);
    if (preciso > th.clientWidth) ajustes.push({ i, preciso });
  });
  for (const { i, preciso } of ajustes) {
    const col = colgroup.children[i] as HTMLElement | undefined;
    if (col) col.style.width = `${Math.ceil(preciso)}px`;
  }
}

/**
 * Ajusta a coluna de ações para os botões caberem lado a lado.
 *
 * O CSS não deixa os botões quebrarem linha, então a coluna precisa nascer com
 * a largura do conteúdo. Os rótulos mudam por tela e até por linha ("Inativar"
 * vs "Reativar"), e chutar um número em pixels dá exatamente o defeito que a
 * medição evita: botões espremidos um debaixo do outro. Medimos o que o
 * navegador desenhou — os botões não encolhem (`flex: 0 0 auto` no CSS), então
 * `offsetWidth` é a largura de verdade.
 */
function ajustarColunaAcoes(
  raiz: HTMLElement,
  colgroup: HTMLElement,
  jaTemLargura: (i: number) => boolean,
): void {
  if (!raiz.isConnected) return;
  const celulas = [...raiz.querySelectorAll<HTMLTableCellElement>("tbody td.acoes")];
  if (celulas.length === 0) return;
  const idx = celulas[0]!.cellIndex;
  if (idx < 0 || jaTemLargura(idx)) return;
  let preciso = 0;
  for (const td of celulas) {
    const filhos = [...td.children] as HTMLElement[];
    if (filhos.length === 0) continue;
    const estilo = getComputedStyle(td);
    // da borda esquerda do 1º botão à direita do último: já inclui o respiro
    // entre eles, venha de margem ou de gap
    const largura =
      filhos[filhos.length - 1]!.getBoundingClientRect().right - filhos[0]!.getBoundingClientRect().left;
    preciso = Math.max(
      preciso,
      Math.ceil(largura) +
        (parseFloat(estilo.paddingLeft) || 0) +
        (parseFloat(estilo.paddingRight) || 0),
    );
  }
  if (preciso <= 0) return;
  const col = colgroup.children[idx] as HTMLElement | undefined;
  if (col) col.style.width = `${Math.ceil(preciso) + 1}px`;
}

/** Opções da tabela paginada. */
export interface OpcoesTabela {
  /**
   * Total de registros **no servidor**. Só para listas paginadas lá (o Log de
   * Auditoria, ADR-0012): `linhas` já são a página atual, o componente não
   * fatia nem reordena, e `aoMudar` deve buscar a página nova.
   */
  totalExterno?: number;
  /**
   * Linha de total, como um `<tr>` já pronto (mesma forma que `linhas`) —
   * quem chama sabe as colunas e o que somar, `tabelaPaginada` só a encaixa
   * num `<tfoot>`, parte da própria tabela. Soma o **filtro inteiro**, não só
   * a página visível: quem chama passa a linha já calculada sobre a lista
   * completa, não sobre `linhas`/`daPagina`.
   */
  linhaTotal?: HTMLElement;
}

/**
 * Tabela padrão com: ordenação por coluna (clique no cabeçalho),
 * redimensionamento das colunas (arrastar a divisória), e rodapé de paginação
 * (20/50/100 por página, ir para primeira/última). `linhas` são todos os `<tr>`
 * já construídos. `id` identifica a tabela para lembrar as larguras.
 *
 * `opts.totalExterno` inverte a paginação: a lista já vem paginada de fora
 * (só a Auditoria, ADR-0012) e `aoMudar` é quem busca a página nova.
 */
/**
 * Coluna da tabela. String = só o rótulo. Objeto:
 * - `ord`: liga/desliga a ordenação (padrão: liga se o rótulo não for vazio).
 * - `w`: largura padrão (número = px, ou string CSS). O usuário ainda pode
 *   redimensionar; o valor manual, uma vez salvo, tem prioridade.
 */
export type Coluna = string | { t: string; ord?: boolean; w?: number | string };

/** O rótulo de uma `Coluna`, nas duas formas que ela aceita. */
export function rotuloColuna(c: Coluna): string {
  return typeof c === "string" ? c : c.t;
}

export function tabelaPaginada(
  id: string,
  colunas: readonly Coluna[],
  linhas: readonly HTMLElement[],
  est: EstadoTabela,
  aoMudar: () => void,
  opts: OpcoesTabela = {},
): HTMLElement {
  const externa = opts.totalExterno != null;
  const headers = colunas.map(rotuloColuna);
  // colunas com cabeçalho vazio (ações) não ordenam; pode-se desligar com {ord:false}
  const ordenavel = colunas.map((c, i) =>
    typeof c === "string" ? headers[i]!.trim() !== "" : (c.ord ?? c.t.trim() !== ""),
  );

  const ordenadas =
    !externa && est.ordCol != null && ordenavel[est.ordCol]
      ? [...linhas].sort((ra, rb) => {
          const c = comparar(
            valorOrdenacao(ra, est.ordCol!),
            valorOrdenacao(rb, est.ordCol!),
          );
          return est.ordDir === "asc" ? c : -c;
        })
      : linhas;

  const total = opts.totalExterno ?? ordenadas.length;
  const totalPaginas = Math.max(1, Math.ceil(total / est.size));
  if (est.page > totalPaginas) est.page = totalPaginas;
  if (est.page < 1) est.page = 1;
  const ini = (est.page - 1) * est.size;
  const fim = externa ? Math.min(ini + ordenadas.length, total) : Math.min(ini + est.size, total);
  // na paginação externa as linhas recebidas JÁ são a página
  const daPagina = externa ? ordenadas : ordenadas.slice(ini, fim);

  // --- cabeçalho com ordenação + handles de redimensionamento ---
  // Sempre lê o que está salvo (é a base do `salvarLarguras`, pra um arrasto
  // não apagar as outras colunas), mas no celular NÃO aplica: lá a tabela rola
  // na horizontal com a largura natural.
  const larguras = lerLarguras(id);
  const usarSalvas = !telaEstreita();
  const colgroup = el("colgroup");
  headers.forEach((_, i) => {
    const c = el("col");
    const col = colunas[i];
    const wConfig = typeof col === "object" ? col.w : undefined;
    if (usarSalvas && larguras[i]) c.style.width = `${larguras[i]}px`;
    else if (wConfig != null)
      c.style.width = typeof wConfig === "number" ? `${wConfig}px` : wConfig;
    else if (i === headers.length - 1 && headers[i]!.trim() === "")
      c.style.width = "160px";
    else if (i === 0) c.style.width = "22%";
    colgroup.append(c);
  });

  const ths = headers.map((h, i) => {
    // Coluna de ações (cabeçalho vazio): marca o `<th>` para o CSS grudá-la à
    // direita quando a tabela rola na horizontal — os botões nunca somem.
    const classes = [
      ordenavel[i] ? "ord" : "",
      h.trim() === "" ? "col-acoes" : "",
    ]
      .filter(Boolean)
      .join(" ");
    const th = el("th", classes ? { class: classes } : {});
    // Ordenável vira <button> de verdade (teclado grátis); coluna comum
    // continua <span> — não tem o que ativar (achado de acessibilidade:
    // antes disso era <span> com só onclick, mouse/toque apenas, em toda
    // tabela do sistema).
    const rotulo = ordenavel[i]
      ? el("button", { type: "button", class: "th-rotulo" }, h)
      : el("span", { class: "th-rotulo" }, h);
    th.append(rotulo);
    if (ordenavel[i]) {
      if (est.ordCol === i) {
        rotulo.append(
          el("span", { class: "th-seta" }, est.ordDir === "asc" ? " ▲" : " ▼"),
        );
      }
      rotulo.addEventListener("click", () => {
        if (est.ordCol === i) {
          est.ordDir = est.ordDir === "asc" ? "desc" : "asc";
        } else {
          est.ordCol = i;
          est.ordDir = "asc";
        }
        aoMudar();
      });
    }
    // divisória de redimensionamento — em todas as colunas, inclusive a última.
    // Mesmo padrão de teclado do .painel-resize (Lançar OS): role=separator +
    // setas — achado de acessibilidade, antes só mousedown.
    const handle = el("div", {
      class: "th-resize",
      role: "separator",
      "aria-orientation": "vertical",
      "aria-label": h.trim() === "" ? "Redimensionar coluna" : `Redimensionar coluna ${h}`,
      tabindex: "0",
    });
    const redimensionar = (delta: number): void => {
      const col = colgroup.children[i] as HTMLElement;
      const w0 = col.getBoundingClientRect().width || th.getBoundingClientRect().width;
      const w = Math.max(60, w0 + delta);
      col.style.width = `${w}px`;
      larguras[i] = w;
      salvarLarguras(id, larguras);
    };
    handle.addEventListener("keydown", (ev: KeyboardEvent) => {
      const passo = ev.shiftKey ? 40 : 12;
      if (ev.key === "ArrowRight") redimensionar(passo);
      else if (ev.key === "ArrowLeft") redimensionar(-passo);
      else return;
      ev.preventDefault();
    });
    handle.addEventListener("mousedown", (ev: MouseEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      const col = colgroup.children[i] as HTMLElement;
      const x0 = ev.clientX;
      const w0 = col.getBoundingClientRect().width || th.getBoundingClientRect().width;
      const mover = (e: MouseEvent): void => {
        col.style.width = `${Math.max(60, w0 + e.clientX - x0)}px`;
      };
      const soltar = (): void => {
        document.removeEventListener("mousemove", mover);
        document.removeEventListener("mouseup", soltar);
        // clique sem arrastar deixa `style.width` vazio — não grava NaN
        const w = parseInt(col.style.width, 10);
        if (Number.isFinite(w)) {
          larguras[i] = w;
          salvarLarguras(id, larguras);
        }
      };
      document.addEventListener("mousemove", mover);
      document.addEventListener("mouseup", soltar);
    });
    th.append(handle);
    return th;
  });

  // No card mobile (CSS abaixo de 600px em styles.css), cada <td> vira uma
  // linha "rótulo: valor" — sem isso o número fica solto, sem dizer o que é
  // (achado da revisão de Lançar OS, mas o defeito é do componente
  // compartilhado, vale pra toda tabela do sistema). `data-th` só entra
  // quando o cabeçalho da coluna não é vazio (coluna de ações se descreve
  // sozinha pelos próprios botões). Só corre sobre `daPagina` — a linha de
  // total (`opts.linhaTotal`, abaixo) tem `colspan`, então o índice de um
  // `<td>` dela não corresponde à mesma coluna de `headers`; ela rotula os
  // próprios `<td>` sozinha, em `linhaTotalTabela`.
  for (const tr of daPagina) {
    const tds = tr.querySelectorAll(":scope > td");
    tds.forEach((td, i) => {
      const rotulo = headers[i];
      if (rotulo && rotulo.trim() !== "") td.setAttribute("data-th", rotulo);
    });
  }

  const tabela = el(
    "div",
    { class: "table-wrap" },
    el(
      "table",
      { class: "grid grid-fixa" },
      colgroup,
      el("thead", {}, el("tr", {}, ...ths)),
      el("tbody", {}, ...daPagina),
      opts.linhaTotal ? el("tfoot", {}, opts.linhaTotal) : null,
    ),
  );

  if (usarSalvas && typeof requestAnimationFrame === "function") {
    const jaTemLargura = (i: number): boolean => {
      const col = colunas[i];
      return larguras[i] != null || (typeof col === "object" && col.w != null);
    };
    requestAnimationFrame(() => {
      ajustarColunaAcoes(tabela, colgroup, jaTemLargura);
      ajustarCabecalhos(tabela, colgroup, jaTemLargura);
    });
  }

  // --- rodapé ---
  const selSize = el("select", {}) as HTMLSelectElement;
  for (const n of TAMANHOS) {
    const o = el("option", { value: String(n) }, `${n} por página`);
    if (n === est.size) o.selected = true;
    selSize.append(o);
  }
  selSize.addEventListener("change", () => {
    est.size = Number(selSize.value);
    est.page = 1;
    aoMudar();
  });

  const info = el(
    "span",
    { class: "muted" },
    total === 0 ? "Nenhum registro" : `${ini + 1}–${fim} de ${total}`,
  );

  const rodapeFilhos: Node[] = [selSize, info];
  if (totalPaginas > 1) {
    const btn = (txt: string, alvo: number, desativa: boolean): HTMLButtonElement => {
      const b = botao("secundario", {}, txt);
      b.disabled = desativa;
      b.addEventListener("click", () => {
        est.page = alvo;
        aoMudar();
      });
      return b;
    };
    rodapeFilhos.push(
      el(
        "span",
        { class: "tabela-pager" },
        btn("«", 1, est.page <= 1),
        btn("‹ Anterior", est.page - 1, est.page <= 1),
        el("span", { class: "muted" }, `Página ${est.page} de ${totalPaginas}`),
        btn("Próxima ›", est.page + 1, est.page >= totalPaginas),
        btn("»", totalPaginas, est.page >= totalPaginas),
      ),
    );
  }

  return el(
    "div",
    { class: "tabela-paginada" },
    tabela,
    el("div", { class: "tabela-rodape" }, ...rodapeFilhos),
  );
}

/** Um valor de `linhaTotalTabela`: `rotulo` vira o `data-th` da célula no
 * card mobile (a própria linha de total rotula os próprios `<td>` — ver
 * comentário no laço de `data-th`, acima, sobre por que não dá pra deixar
 * `tabelaPaginada` inferir isso pela posição quando há `colspan`). */
export interface ValorTotal {
  rotulo: string;
  texto: string;
}

/**
 * Uma linha de total pronta pra `OpcoesTabela.linhaTotal`: `rotulo` ocupa as
 * colunas antes do primeiro valor (`colspan`), um `<td class="num">` por
 * valor (com o próprio `data-th`), e um `<td>` final ocupa o que sobrar —
 * mesma ideia de `colspan` que a Planilha de Fechamento já usa pro "Total
 * geral", em vez de contar coluna por coluna e enumerar `<td>` vazio um por
 * um toda vez que a tabela ganha ou perde uma coluna.
 */
export function linhaTotalTabela(
  totalColunas: number,
  rotuloColspan: number,
  rotulo: string,
  valores: readonly ValorTotal[],
): HTMLElement {
  const depois = totalColunas - rotuloColspan - valores.length;
  return el(
    "tr",
    {},
    el("td", { colspan: String(rotuloColspan), class: "linha-total-rotulo" }, rotulo),
    ...valores.map((v) => el("td", { class: "num", "data-th": v.rotulo }, v.texto)),
    ...(depois > 0 ? [el("td", { colspan: String(depois) })] : []),
  );
}

// --- painel redimensionável (layouts mestre-detalhe) ---------------------

/**
 * Ciclo padrão de "segurar o mouse e arrastar": ouve `mousedown` em `alvo`,
 * registra `mousemove`/`mouseup` em `document` enquanto dura o arrasto e
 * limpa os dois ao soltar — a mesma máquina por trás de arrastar/redimensionar
 * um modal (`openModal`) e redimensionar o painel lateral, aqui embaixo.
 * Botão diferente do esquerdo (`ev.button !== 0`) não inicia nada.
 * `aoComecar` devolvendo `false` cancela (ex.: clique caiu no botão de
 * fechar do modal, não no cabeçalho de arrastar).
 */
function comArrastoDoMouse(
  alvo: HTMLElement,
  opts: {
    aoComecar: (ev: MouseEvent) => boolean | void;
    aoMover: (ev: MouseEvent) => void;
    aoSoltar?: () => void;
    pararPropagacao?: boolean;
  },
): void {
  alvo.addEventListener("mousedown", (ev: MouseEvent) => {
    if (ev.button !== 0) return;
    if (opts.aoComecar(ev) === false) return;
    ev.preventDefault();
    if (opts.pararPropagacao) ev.stopPropagation();
    const mover = (e: MouseEvent): void => opts.aoMover(e);
    const soltar = (): void => {
      document.removeEventListener("mousemove", mover);
      document.removeEventListener("mouseup", soltar);
      opts.aoSoltar?.();
    };
    document.addEventListener("mousemove", mover);
    document.addEventListener("mouseup", soltar);
  });
}

/**
 * Divisória arrastável para o painel da direita num layout mestre-detalhe
 * (ex.: lista + ficha de Lançar OS). Cada tela guarda a sua escolha, por
 * `chave`, e ela volta do jeito que a pessoa deixou.
 *
 * O que se guarda é a **proporção** do painel na largura do layout (0–1),
 * não pixels: a mesma divisão vale num notebook e num monitor 4K, e a tela
 * inteira é sempre aproveitada. Ela vai para `--painel-p` no próprio
 * `painel`; o CSS (`clamp()`) garante os mínimos em px dos dois lados.
 *
 * Retorna o elemento da divisória; insira-o entre a lista e `opts.painel`.
 */
export function painelRedimensionavel(opts: {
  chave: string;
  painel: HTMLElement;
  /** Proporção inicial do painel, 0–1. */
  padrao?: number;
}): HTMLElement {
  const MIN = 0.25;
  const MAX = 0.75;
  const padrao = opts.padrao ?? 0.5;
  const chaveStorage = `painel-proporcao:${opts.chave}`;

  const ler = (): number =>
    lerJSONValidado<number>(
      chaveStorage,
      (v): v is number => typeof v === "number" && Number.isFinite(v) && v >= MIN && v <= MAX,
    ) ?? padrao;
  const salvar = (p: number): void => salvarJSON(chaveStorage, p);
  const limitar = (p: number): number => clamp(p, MIN, MAX);
  const aplicar = (p: number): void => {
    opts.painel.style.setProperty("--painel-p", `${(p * 100).toFixed(2)}%`);
  };
  const larguraLayout = (): number => opts.painel.parentElement?.getBoundingClientRect().width || 1;

  let atual = ler();
  aplicar(atual);

  const handle = el("div", {
    class: "painel-resize",
    role: "separator",
    "aria-orientation": "vertical",
    "aria-label": "Redimensionar painel",
    tabindex: "0",
  });

  let w0 = 0;
  let x0 = 0;
  let total = 1;
  comArrastoDoMouse(handle, {
    aoComecar: (ev) => {
      x0 = ev.clientX;
      w0 = opts.painel.getBoundingClientRect().width;
      total = larguraLayout();
      handle.classList.add("arrastando");
    },
    aoMover: (e) => {
      // arrastar para a esquerda alarga o painel (a borda da esquerda dele
      // acompanha o mouse), por isso `x0 - e.clientX` e não o contrário.
      atual = limitar((w0 + (x0 - e.clientX)) / total);
      aplicar(atual);
    },
    aoSoltar: () => {
      handle.classList.remove("arrastando");
      salvar(atual);
    },
  });

  handle.addEventListener("keydown", (ev: KeyboardEvent) => {
    const passo = ev.shiftKey ? 0.06 : 0.02;
    if (ev.key === "ArrowLeft") atual = limitar(atual + passo);
    else if (ev.key === "ArrowRight") atual = limitar(atual - passo);
    else return;
    ev.preventDefault();
    aplicar(atual);
    salvar(atual);
  });

  return handle;
}

const FOCAVEIS = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Registro de "portais flutuantes" abertos — hoje só a lista de um
 * `multiSelect` (`_multi-select.ts`) escapando de um ancestral com
 * `overflow` (ex.: `.modal-body`/`.modal-box` cortando o combobox no meio,
 * achado na tela de Cliente). Um elemento assim sai do lugar de origem no
 * DOM (vira filho de `<body>`, `position: fixed`) pra escapar do corte
 * visual — mas isso também o tira de dentro de `.modal-box`, então a prisão
 * de foco do modal (abaixo) para de encontrá-lo sozinha. Este registro é
 * como ela volta a encontrar.
 */
const portaisFlutuantesAbertos = new Set<HTMLElement>();
/** Registra um portal flutuante pra prisão de foco de modal alcançar seus
 * elementos focáveis. Devolve a função que desregistra (chame ao fechar). */
export function registrarPortalFlutuante(el: HTMLElement): () => void {
  portaisFlutuantesAbertos.add(el);
  return () => portaisFlutuantesAbertos.delete(el);
}

/**
 * Abre um modal centralizado. Retorna uma função que o fecha.
 *
 * O modal só fecha por ação explícita de botão (o "×", "Cancelar", "Salvar"
 * etc.). Clicar fora da caixa ou apertar Esc não fecha — evita perder um
 * formulário meio preenchido por um clique perdido. Padrão da aplicação.
 *
 * Foco: entra na caixa ao abrir (primeiro campo/botão), fica preso lá
 * dentro (Tab não escapa pro conteúdo atrás do overlay) e volta pra quem
 * abriu o modal ao fechar — achado de acessibilidade, antes o foco ficava
 * onde estivesse quando o overlay some.
 */
export function openModal(
  titulo: string,
  conteudo: (fechar: () => void) => Node,
  largura: "padrao" | "largo" | "amplo" = "padrao",
  aoFechar?: () => void,
): () => void {
  const elementoAnterior = document.activeElement as HTMLElement | null;
  const tituloId = `modal-titulo-${crypto.randomUUID()}`;

  // Um combobox (`multiSelect`) aberto em qualquer lugar da tela — inclusive
  // fora deste modal — tem sua lista portada em `<body>` (ver
  // `registrarPortalFlutuante`) e ficaria registrado como focável por ESTE
  // modal mesmo sem relação nenhuma com ele, e visualmente escondido atrás
  // do overlay (`.ms-lista` tem z-index menor). Fecha geral ao abrir, pelo
  // próprio evento `toggle` do combobox — reaproveita a limpeza que ele já
  // faz sozinho (listeners de scroll/resize, desregistro do portal).
  document.querySelectorAll<HTMLDetailsElement>("details.multi-select[open]").forEach((d) => {
    d.open = false;
  });

  const fechar = (): void => {
    // Mesma limpeza, agora só dentro deste modal — cobre fechar (Cancelar/×)
    // com o combobox do próprio formulário ainda aberto, que senão deixaria
    // a lista (portada em `<body>`) viva pra sempre, sem o modal que a criou.
    caixa.querySelectorAll<HTMLDetailsElement>("details.multi-select[open]").forEach((d) => {
      d.open = false;
    });
    window.removeEventListener("resize", reclampNoRedimensionar);
    observarConteudo.disconnect();
    overlay.remove();
    document.removeEventListener("keydown", prenderFoco);
    if (aoFechar) aoFechar();
    elementoAnterior?.focus?.();
  };
  const botaoFechar = el(
    "button",
    { type: "button", class: "modal-x", "aria-label": "Fechar" },
    icon("close", 16, { peso: 2.2 }),
  );
  botaoFechar.addEventListener("click", fechar);
  const head = el(
    "div",
    { class: "modal-head" },
    el("h2", { id: tituloId }, titulo),
    botaoFechar,
  );
  const corpo = el("div", { class: "modal-body" }, conteudo(fechar));
  const caixa = el(
    "div",
    {
      class: `modal-box modal-${largura}`,
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": tituloId,
      tabindex: "-1",
    },
    head,
    corpo,
  );
  // --- arrastar (cabeçalho) e redimensionar (canto) -----------------------
  // Todo modal, no lugar compartilhado — nunca tela a tela. Lembra posição e
  // largura entre uma abertura e outra (mesmo padrão das larguras de coluna
  // de tabela, via localStorage), pela chave do título: um título dinâmico
  // ("OS 1042") não compartilha posição com outro ("OS 1043"), mas é a
  // maioria dos modais grandes (os que valem a pena redimensionar) que têm
  // título fixo, então isso cobre bem o caso real sem inventar um `id`
  // próprio que toda chamada de `openModal` precisaria passar.
  //
  // A ALTURA nunca é fixada: acompanha o conteúdo (uma seção que abre ou
  // fecha dentro do modal o faz crescer ou encolher sozinho) com teto na
  // borda de baixo da janela; passou disso, o corpo rola. Por isso o canto só
  // redimensiona a largura. Posições salvas antes disso ainda trazem um
  // `height` — é ignorado.
  const chavePos = `modal-pos:${titulo}`;
  interface PosModal { top: number; left: number; width: number }
  const validoPosModal = (v: unknown): v is PosModal =>
    v !== null && typeof v === "object" &&
    typeof (v as PosModal).top === "number" && typeof (v as PosModal).left === "number" &&
    typeof (v as PosModal).width === "number";
  const lerPosSalva = (): PosModal | null => lerJSONValidado(chavePos, validoPosModal);
  const salvarPos = (p: PosModal): void => {
    salvarJSON(chavePos, p);
    // Teto pra não crescer pra sempre: título com dado dinâmico ("Cancelar a
    // OS 1042") grava uma chave nova por registro — sem limite, uma
    // instalação de longa duração acumula posição de OS que nunca mais vai
    // abrir de novo. Remove as mais antigas (ordem de inserção) quando passa
    // do teto; não é LRU exato, mas é o suficiente pra não crescer sem fim,
    // sem complicar o que é só um "lembrete de conforto".
    try {
      const TETO = 60;
      const chaves = Object.keys(localStorage).filter((k) => k.startsWith("modal-pos:"));
      if (chaves.length > TETO) {
        for (const k of chaves.slice(0, chaves.length - TETO)) localStorage.removeItem(k);
      }
    } catch {
      /* sem persistência é ok */
    }
  };

  // `larguraPx`, não `largura`: o parâmetro `largura` da função (a
  // variante "padrao"/"largo"/"amplo") já usa esse nome.
  let larguraPx = 0;
  /** O topo que a pessoa escolheu (arrastando); o aplicado pode estar acima. */
  let topoPreferido = 0;
  /** Folga entre a caixa e a borda de baixo (e direita) da janela. */
  const folgaJanela = 8;
  /**
   * Altura automática: a caixa fica no topo escolhido enquanto o conteúdo
   * cabe abaixo dele; quando não cabe, SOBE o necessário (nunca acima do
   * topo da janela) — senão um modal arrastado para baixo ficava só com o
   * cabeçalho. O teto é sempre a borda de baixo; passou disso, o corpo rola.
   */
  const posicionarNaVertical = (): void => {
    if (caixa.style.position !== "fixed") return;
    const natural = head.offsetHeight + corpo.scrollHeight;
    const top = Math.max(0, Math.min(topoPreferido, window.innerHeight - folgaJanela - natural));
    caixa.style.top = `${top}px`;
    caixa.style.height = "";
    caixa.style.maxHeight = `${Math.max(0, window.innerHeight - top - folgaJanela)}px`;
  };
  const fixarCaixa = (top: number, left: number, w: number): void => {
    larguraPx = w;
    topoPreferido = top;
    caixa.style.position = "fixed";
    caixa.style.margin = "0";
    caixa.style.left = `${left}px`;
    caixa.style.width = `${w}px`;
    caixa.style.maxWidth = "none";
    posicionarNaVertical();
  };
  const salvarEstadoAtual = (): void => {
    const left = parseFloat(caixa.style.left);
    if (Number.isNaN(left)) return;
    salvarPos({ top: topoPreferido, left, width: larguraPx });
  };
  // conteúdo que cresce/encolhe (uma seção que abre ou fecha) reposiciona
  const observarConteudo = new ResizeObserver(() => posicionarNaVertical());
  for (const filho of corpo.children) observarConteudo.observe(filho);

  // Nunca deixa o topo/esquerda saírem da janela (só `margem` px do modal
  // precisam continuar alcançáveis) nem a caixa maior que ela.
  const margemJanela = 80;
  const clampTop = (top: number): number => clamp(top, 0, Math.max(0, window.innerHeight - margemJanela));
  const clampLeft = (left: number): number => clamp(left, 0, Math.max(0, window.innerWidth - margemJanela));

  const salva = lerPosSalva();
  if (salva) {
    // clampa contra a janela atual — se ela encolheu desde a última vez
    // (troca de monitor, por ex.), não deixa o modal nascer fora de vista.
    fixarCaixa(clampTop(salva.top), clampLeft(salva.left), Math.min(salva.width, window.innerWidth - 24));
  }

  // Um modal arrastado/redimensionado (position: fixed, em pixels) não
  // reflui como o layout normal — se a janela encolher DEPOIS (desconectar
  // um monitor externo, restaurar de maximizado) enquanto ele está aberto,
  // nada o recoloca dentro da tela sozinho. Reclampa a cada resize da
  // janela; `fechar()` (acima) remove este listener.
  const reclampNoRedimensionar = (): void => {
    if (caixa.style.position !== "fixed") return;
    fixarCaixa(
      clampTop(topoPreferido),
      clampLeft(parseFloat(caixa.style.left) || 0),
      Math.min(larguraPx, window.innerWidth - 24),
    );
  };
  window.addEventListener("resize", reclampNoRedimensionar);

  let offX = 0;
  let offY = 0;
  comArrastoDoMouse(head, {
    aoComecar: (ev) => {
      if ((ev.target as HTMLElement).closest(".modal-x")) return false;
      const r = fixarSeSolta();
      offX = ev.clientX - r.left;
      offY = ev.clientY - r.top;
      caixa.classList.add("modal-arrastando");
    },
    aoMover: (e) => {
      fixarCaixa(clampTop(e.clientY - offY), clampLeft(e.clientX - offX), larguraPx);
    },
    aoSoltar: () => {
      caixa.classList.remove("modal-arrastando");
      salvarEstadoAtual();
    },
  });

  // `left` explícito (não relido de `getBoundingClientRect`, que só muda de
  // fato ao arrastar, nunca ao redimensionar): evita forçar reflow síncrono a
  // cada pixel de `mousemove`. O topo é o escolhido (`topoPreferido`) —
  // redimensionar a largura não mexe nele.
  const redimensionarPara = (left: number, w: number): void => {
    const minW = 320;
    fixarCaixa(topoPreferido, left, Math.min(window.innerWidth - left - folgaJanela, Math.max(minW, w)));
  };
  const fixarSeSolta = (): DOMRect => {
    const r = caixa.getBoundingClientRect();
    if (caixa.style.position !== "fixed") fixarCaixa(r.top, r.left, r.width);
    return r;
  };
  // só a largura (a altura segue o conteúdo): separador que se move na
  // horizontal é, em ARIA, de orientação vertical
  const handleResize = el("div", {
    class: "modal-resize",
    role: "separator",
    "aria-orientation": "vertical",
    "aria-label": `Redimensionar ${titulo}`,
    tabindex: "0",
  });
  let r0 = { left: 0, width: 0 };
  let x0 = 0;
  comArrastoDoMouse(handleResize, {
    pararPropagacao: true,
    aoComecar: (ev) => {
      const r = fixarSeSolta();
      r0 = { left: r.left, width: r.width };
      x0 = ev.clientX;
    },
    aoMover: (e) => redimensionarPara(r0.left, r0.width + (e.clientX - x0)),
    aoSoltar: () => salvarEstadoAtual(),
  });
  handleResize.addEventListener("keydown", (ev: KeyboardEvent) => {
    const passo = ev.shiftKey ? 40 : 12;
    if (ev.key !== "ArrowRight" && ev.key !== "ArrowLeft") return;
    const r = fixarSeSolta();
    redimensionarPara(r.left, r.width + (ev.key === "ArrowRight" ? passo : -passo));
    ev.preventDefault();
    salvarEstadoAtual();
  });
  caixa.append(handleResize);

  const prenderFoco = (ev: KeyboardEvent): void => {
    if (ev.key !== "Tab") return;
    // Um portal flutuante aberto (combobox escapando de `.modal-box`, ver
    // `registrarPortalFlutuante`) saiu do DOM da caixa — sem isso a prisão
    // de foco não o alcança, e o Tab pula ele por inteiro.
    const focaveis = [
      ...caixa.querySelectorAll<HTMLElement>(FOCAVEIS),
      ...[...portaisFlutuantesAbertos].flatMap((p) => [...p.querySelectorAll<HTMLElement>(FOCAVEIS)]),
    ];
    if (focaveis.length === 0) return;
    const primeiro = focaveis[0]!;
    const ultimo = focaveis[focaveis.length - 1]!;
    if (ev.shiftKey && document.activeElement === primeiro) {
      ev.preventDefault();
      ultimo.focus();
    } else if (!ev.shiftKey && document.activeElement === ultimo) {
      ev.preventDefault();
      primeiro.focus();
    }
  };
  document.addEventListener("keydown", prenderFoco);
  const overlay = el("div", { class: "modal-overlay" }, caixa);
  document.body.append(overlay);
  // espera o conteúdo estar no DOM antes de medir o que é focável
  requestAnimationFrame(() => {
    const primeiro = caixa.querySelector<HTMLElement>(FOCAVEIS);
    (primeiro ?? caixa).focus();
  });
  return fechar;
}

let dicaSeq = 0;
/**
 * Ícone "i" com uma explicação curta (ex.: como um indicador é calculado).
 * Aparece ao passar o mouse, ao focar pelo teclado e ao tocar (celular, onde
 * não existe "passar o mouse"); some ao sair, com Esc ou tocando de novo.
 */
export function dicaInfo(rotulo: string, ...conteudo: Child[]): HTMLElement {
  const id = `dica-${++dicaSeq}`;
  const balao = el("span", { class: "dica-balao", role: "tooltip", id }, ...conteudo);
  const botao = el(
    "button",
    { type: "button", class: "dica-botao", "aria-label": rotulo, "aria-describedby": id, "aria-expanded": "false" },
    icon("info", 16),
  );
  const caixa = el("span", { class: "dica" }, botao, balao);
  /** Puxa o balão para a esquerda o quanto for preciso para caber na tela (celular). */
  const caber = (): void => {
    balao.style.left = "";
    requestAnimationFrame(() => {
      const r = balao.getBoundingClientRect();
      const sobra = r.right - (document.documentElement.clientWidth - 8);
      if (sobra > 0) balao.style.left = `${-8 - sobra}px`;
    });
  };
  const abrir = (sim: boolean): void => {
    caixa.classList.toggle("dica-aberta", sim);
    botao.setAttribute("aria-expanded", String(sim));
    if (sim) caber();
  };
  caixa.addEventListener("mouseenter", caber);
  botao.addEventListener("focus", caber);
  botao.addEventListener("click", () => abrir(!caixa.classList.contains("dica-aberta")));
  botao.addEventListener("blur", () => abrir(false));
  botao.addEventListener("keydown", (e) => {
    if (e.key === "Escape") abrir(false);
  });
  return caixa;
}
